import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { usePostHog } from '@posthog/react';
import {
  Badge,
  ChevronLeftIcon,
  ChevronRightIcon,
  CommentIcon,
  ExternalLinkIcon,
  FollowersIcon,
  InboxIcon,
  Logo,
  NavAccessCard,
  NavEntry,
  NavSection,
  PencilIcon,
  PlayIcon,
  QuoteIcon,
  SaveIcon,
  ShareIcon,
  SparkleIcon,
  TranscriptIcon
} from '@viniciomendesr/rayrovisk-design-system';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { instagramHandle } from '../api/handle';
import { useMe, useProfile, useSessionAvatarKey } from '../api/queries';
import { useSession } from '../auth/SessionProvider';
import type { Me, ProductId } from '../api/types';
import {
  areaBadge,
  areaLanding,
  entryAccess,
  entryCount,
  purchasableAreas,
  visibleAreas,
  visibleEntries,
  type NavArea,
  type NavItem
} from './navigation';

/**
 * Icons live here rather than in the manifest: the manifest is data the tests
 * read, and a `ReactNode` in it would make every persona fixture carry JSX for
 * no reason. Keyed by entry id, so an entry without one simply has none.
 *
 * Exported for the one assertion that keeps this table and the manifest in
 * step. "Simply has none" is the mechanism, and on 2026-08-18 it was also the
 * defect: adding an entry over there and forgetting this table draws a row
 * whose label starts where every other row's icon does.
 */
export const ICONS: Record<string, ReactNode> = {
  biblioteca: <InboxIcon size={15} />,
  montar: <PlayIcon size={15} />,
  'gerar-ia': <SparkleIcon size={15} />,
  fixados: <SaveIcon size={15} />,
  perfil: <FollowersIcon size={15} />,
  roteiros: <TranscriptIcon size={15} />,
  // The same glyph the screen puts on its own `Minhas redes` heading. It was
  // missing from 2026-08-18, when the entry was added to the manifest and this
  // table was not: `NavEntry` draws no leading slot without one, so the newest
  // row was the only one whose label started at the edge.
  'midia-kit': <ShareIcon size={15} />,
  // The mock's glyphs, kept: a spark for what she has not thought of yet, the
  // quotation marks for what she said before, a speech bubble for something
  // that answers.
  ideias: <SparkleIcon size={15} />,
  memorias: <QuoteIcon size={15} />,
  agentes: <CommentIcon size={15} />
};

/** Below this the 288px nav stops leaving a main column worth reading. */
const SHELL_BREAKPOINT = 900;

/**
 * The chrome behind every signed-in screen.
 *
 * It renders a manifest (`./navigation`) rather than a hardcoded list, and
 * every lock, badge and counter is derived from that manifest plus `/v1/me`.
 * Nothing is derived from the URL any more: the sub-section used to appear only
 * when the route already matched, which meant an entry could never say whether
 * she owned it — the shell had to be standing inside an area to draw it at all.
 *
 * **Appearance, never authority.** Every route keeps its server-side gate. A
 * padlock here is a courtesy; the 403 behind it is the boundary, and a locked
 * entry navigates rather than refusing precisely so the server gets to answer.
 *
 * Its geometry lives in `app.css`, because the widths it needs are answers to
 * media queries and an inline style cannot ask one.
 */
export function AppShell(): JSX.Element {
  // Open is still the desktop default; a phone opens onto the content instead
  // of onto a menu that would take three quarters of the screen.
  const [open, setOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth > SHELL_BREAKPOINT
  );
  const me = useMe();
  /**
   * For the handle in the footer, and nothing else. It is the same cached query
   * the generator screens already run, so on those it costs no request; on the
   * library it costs one, and it fails quietly for anyone the profile route
   * refuses — the footer simply draws no handle rather than an error.
   */
  const profile = useProfile();
  const sessionAvatar = useSessionAvatarKey();
  const avatarKey =
    sessionAvatar.data?.thumbnailKey ?? profile.data?.profile?.identity.instagramThumbnailKey;
  const { signOut } = useSession();
  const posthog = usePostHog();
  const areas = visibleAreas();
  const bought = purchasableAreas();

  /**
   * Who she is to a funnel, set here because this is the first screen inside
   * the session that holds `/v1/me`. `SessionProvider` identifies her by the
   * Supabase id and knows nothing else.
   *
   * **What she is called is not set here.** The e-mail is the identifier and
   * travels on `identify`, in `SessionProvider` (ADR-0022, amended
   * 2026-08-08). What is left out of both is everything that identifies
   * without being needed to find her: display name, Instagram handle.
   *
   * These properties answer a different question — do buyers of the starter
   * copy less than buyers of the pack, does a zero balance predict a second
   * purchase — and none of them needs a name to do it.
   */
  useEffect(() => {
    if (!me.data) {
      return;
    }
    const products = me.data.products;
    posthog?.setPersonProperties({
      products_granted: (Object.keys(products) as ProductId[]).filter(
        (product) => products[product] === 'granted'
      ),
      script_credits_balance: me.data.scriptCredits.balance,
      script_credits_granted: me.data.scriptCredits.granted,
      pinned_count: me.data.pinnedCount,
      has_password: me.data.hasPassword,
      purchased_at: me.data.purchasedAt
    });
  }, [me.data, posthog]);

  /**
   * Below the shell breakpoint the open nav is a full-width drawer and the
   * content sits underneath it, so a tap that picks a screen has to give that
   * screen back. Above it the nav is a rail beside the content and closing it
   * on every click would be the app tidying up after her.
   */
  function leaveNav() {
    if (window.innerWidth <= SHELL_BREAKPOINT) {
      setOpen(false);
    }
  }

  return (
    <div className="rvk-shell">
      {open && (
        <>
          <button
            type="button"
            className="rvk-shell-backdrop"
            aria-label="Dispensar o menu de acessos"
            onClick={() => setOpen(false)}
          />
          <div className="rvk-shell-nav-placeholder" aria-hidden="true" />
        </>
      )}
      <nav aria-label="Navegação do produto" className="rvk-shell-nav" data-open={open}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'space-between' : 'center',
            gap: 11,
            flex: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
            <Logo variant="symbol" size={34} />
            {open && (
              <span style={{ font: '400 20px/1 var(--rvk-font-expressive)', letterSpacing: '-.01em' }}>
                Roteirovisk
              </span>
            )}
          </div>
          {open && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Fechar o menu de acessos"
              aria-label="Fechar o menu de acessos"
              className="rvk-icon-button"
              style={iconButton}
            >
              <ChevronLeftIcon size={14} />
            </button>
          )}
        </div>

        {!open && (
          <>
            <button
              type="button"
              onClick={() => setOpen(true)}
              title="Abrir o menu de acessos"
              aria-label="Abrir o menu de acessos"
              className="rvk-icon-button"
              style={iconButton}
            >
              <ChevronRightIcon size={14} />
            </button>

            {/* Collapsing buys width, so the written label is what goes — not
                the way in. A rail whose only action is to expand again makes
                the collapsed state cost a click to be useful. The name stays,
                as the accessible name and the tooltip. A locked area stays on
                the rail for the same reason it stays on the list. */}
            <div className="rvk-shell-rail-links">
              {bought.map((area) => (
                <NavLink
                  key={area.id}
                  to={me.data ? areaLanding(area, me.data) : area.entries[0]!.to}
                  title={area.label}
                  aria-label={area.label}
                  className="rvk-icon-button"
                  style={({ isActive }) => ({
                    ...iconButton,
                    color: isActive ? 'var(--rvk-text-primary)' : 'var(--rvk-text-tertiary)',
                    background: isActive ? 'var(--rvk-surface-2)' : 'var(--rvk-surface-1)'
                  })}
                >
                  {ICONS[area.entries[0]!.id]}
                </NavLink>
              ))}
            </div>

            <button
              type="button"
              onClick={() => void signOut()}
              title="Sair da conta"
              aria-label="Sair da conta"
              className="rvk-icon-button"
              style={{ ...iconButton, marginTop: 'auto' }}
            >
              <ExternalLinkIcon size={14} />
            </button>

            {/* Last on the rail, which puts it at the right end of the phone
                strip. It is her own face and needs no label: the row already
                carries four named targets, and a fifth name is noise. */}
            <ProfileAvatar
              objectKey={avatarKey}
              name={me.data?.displayName ?? me.data?.email ?? 'Você'}
              size="sm"
            />
          </>
        )}

        {open && (
          <>
            <NavSection label="Seus acessos" level={2}>
              {bought.map((area) => (
                <AreaCard key={area.id} area={area} me={me.data} onNavigate={leaveNav} />
              ))}
            </NavSection>

            {/* Every area's entries, always — not only the area she is standing
                in. A list that appears on arrival cannot sell what she has not
                bought, and selling it is what these locks are for. */}
            {areas.map((area) => (
              <NavSection key={area.id} label={area.label}>
                {visibleEntries(area).map((item) => (
                  <Entry key={item.id} item={item} me={me.data} onNavigate={leaveNav} />
                ))}
              </NavSection>
            ))}

            <Link
              to="/opiniao"
              onClick={leaveNav}
              style={{
                marginTop: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '9px 11px',
                border: '1px solid var(--rvk-border-subtle)',
                borderRadius: 'var(--rvk-radius-md)',
                font: '500 12.5px/1.3 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              <PencilIcon size={15} />
              <span style={{ minWidth: 0 }}>Deixar uma opinião</span>
            </Link>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                paddingTop: 16,
                borderTop: '1px solid var(--rvk-border-subtle)'
              }}
            >
              <ProfileAvatar
                objectKey={avatarKey}
                name={me.data?.displayName ?? me.data?.email ?? 'Você'}
                size="sm"
              />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                {me.data?.displayName && (
                  <span style={{ font: '500 12.5px/1.3 var(--rvk-font-functional)' }}>
                    {me.data.displayName}
                  </span>
                )}
                {/* Above the e-mail, and stronger than it: the handle is how
                    she recognises her own account, and the address is only how
                    we reach it. Absent for anyone who left the field blank —
                    there is no placeholder, because an empty `@` reads as a
                    broken row rather than as an unanswered question. */}
                {instagramHandle(profile.data?.profile?.identity.instagram) && (
                  <a
                    href={`https://instagram.com/${instagramHandle(
                      profile.data?.profile?.identity.instagram
                    )!.slice(1)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      font: '500 12px/1.3 var(--rvk-font-functional)',
                      color: 'inherit',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {instagramHandle(profile.data?.profile?.identity.instagram)}
                  </a>
                )}
                <span
                  style={{
                    font: '400 11px/1.3 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {me.data?.email ?? ''}
                </span>
              </div>
              {/* Signing out asks nothing: the magic link and the password both
                  reopen the account, so the worst a stray click costs is one
                  e-mail. A confirmation dialog here would guard a door that is
                  never locked. */}
              <button
                type="button"
                onClick={() => void signOut()}
                title="Sair da conta"
                aria-label="Sair da conta"
                className="rvk-icon-button"
                style={iconButton}
              >
                <ExternalLinkIcon size={14} />
              </button>
            </div>
          </>
        )}
      </nav>

      <main className="rvk-shell-main">
        <Outlet />
      </main>
    </div>
  );
}

const iconButton = {
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  border: '1px solid var(--rvk-border-subtle)',
  borderRadius: 'var(--rvk-radius-md)',
  background: 'var(--rvk-surface-1)',
  color: 'var(--rvk-text-tertiary)',
  cursor: 'pointer'
} as const;

/**
 * The badge is resolved here and handed to the card as a slot. The design
 * system never learns that "ativo" and "10 grátis" are two renderings of one
 * entitlement — that is commercial policy, and it lives in the product.
 */
function AreaCard(props: { area: NavArea; me: Me | undefined; onNavigate: () => void }): JSX.Element {
  const badge = props.me ? areaBadge(props.area, props.me) : null;

  return (
    // A plain `Link`, not a `NavLink`, and that is the fix for a real defect
    // rather than a simplification. The card points at the first entry she can
    // open, so its target is always some entry's target too — and `NavLink`
    // marks whatever matches, which put `aria-current="page"` on the card and
    // on the entry at once. A screen reader then announces the same destination
    // as the current page twice. The entry is the precise location and keeps
    // the marker; the card is a way in.
    <NavAccessCard
      as={Link}
      to={props.me ? areaLanding(props.area, props.me) : props.area.entries[0]!.to}
      onClick={props.onNavigate}
      badge={
        badge && (
          <Badge tone={badge.tone} variant={badge.variant} size="sm">
            {badge.text}
          </Badge>
        )
      }
    >
      {props.area.label}
    </NavAccessCard>
  );
}

/**
 * A locked entry still navigates. It lands on its route, the route answers
 * `403` with a state, and `AccessStateScreen` explains — which is the same
 * answer the server already gives and the only one that shows her a price.
 * Refusing the click here would be a second implementation of one rule, and the
 * quieter of the two.
 */
function Entry(props: { item: NavItem; me: Me | undefined; onNavigate: () => void }): JSX.Element {
  const access = props.me ? entryAccess(props.item, props.me) : undefined;

  return (
    <NavEntry
      as={NavLink}
      to={props.item.to}
      onClick={props.onNavigate}
      icon={ICONS[props.item.id]}
      access={access?.access}
      accessReason={access?.reason}
      count={props.me ? entryCount(props.item, props.me) : undefined}
    >
      {props.item.label}
    </NavEntry>
  );
}
